import React, { useCallback, useState, useEffect } from "react";
import { Graphics } from "@pixi/react";

const CornerNode = ({ 
  x, 
  y, 
  index, 
  shape, 
  onNodeMouseDown, 
  onNodeMouseMove,
  onNodeMouseUp,
  containerRef,
  isRotationHandle 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);

  // Create a unique identifier for this node
  const nodeId = `${shape?.id}-${index}`;
  const position = `${Math.round(x)},${Math.round(y)}`;

  // Listen for drag events from other nodes at the same position
  useEffect(() => {
    const handleNodeDragStart = (event) => {
      const { nodeId: draggingNodeId, position: draggingPosition } = event.detail;
      
      // If another node at the same position is being dragged, hide this node
      if (draggingNodeId !== nodeId && draggingPosition === position) {
        setShouldHide(true);
      }
    };

    const handleNodeDragEnd = (event) => {
      const { nodeId: draggingNodeId, position: draggingPosition } = event.detail;
      
      // If the dragging node at our position stopped, show this node again
      if (draggingNodeId !== nodeId && draggingPosition === position) {
        setShouldHide(false);
      }
    };

    // Listen for drag events
    window.addEventListener('nodeDragStart', handleNodeDragStart);
    window.addEventListener('nodeDragEnd', handleNodeDragEnd);

    return () => {
      window.removeEventListener('nodeDragStart', handleNodeDragStart);
      window.removeEventListener('nodeDragEnd', handleNodeDragEnd);
    };
  }, [nodeId, position]);

  // Determine cursor based on node type and position
  const getCursor = () => {
    if (isRotationHandle) return "grab";
    
    // Handle different shape types
    if (shape.nodes) {
      // Line shapes have nodes
      if (index === 0 || index === shape.nodes.length - 1) {
        // End nodes - show horizontal/vertical scaling
        return "ew-resize"; // horizontal scaling
      } else {
        // Middle nodes (side nodes) - show perpendicular scaling cursor
        // Calculate line direction to determine appropriate cursor
        const startNode = shape.nodes[0];
        const endNode = shape.nodes[shape.nodes.length - 1];
        const dx = endNode.x - startNode.x;
        const dy = endNode.y - startNode.y;
        
        // Determine if line is more horizontal or vertical
        if (Math.abs(dx) > Math.abs(dy)) {
          // Horizontal line - side nodes scale vertically
          return "ns-resize";
        } else {
          // Vertical line - side nodes scale horizontally  
          return "ew-resize";
        }
      }
    } else {
      // Rectangle shapes don't have nodes, use index for corner positions
      switch (index) {
        case 0: // top-left
          return "nw-resize";
        case 1: // top-right
          return "ne-resize";
        case 2: // bottom-right
          return "se-resize";
        case 3: // bottom-left
          return "sw-resize";
        default:
          return "pointer";
      }
    }
  };

  const handleMouseDown = useCallback((event) => {
    console.log('🔵 CornerNode handleMouseDown called:', {
      index,
      hasNodes: !!shape?.nodes,
      shapeType: shape?.type,
      shapeId: shape?.id
    });
    
    event.stopPropagation();
    
    // Set dragging state to hide the node
    setIsDragging(true);
    
    // Notify other nodes at the same position to hide
    window.dispatchEvent(new CustomEvent('nodeDragStart', {
      detail: { nodeId, position }
    }));
    
    // Get global coordinates for the node
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else {
      // Convert local to global coordinates
      const stage = containerRef.current?.parent?.parent;
      if (!stage) return;
      
      const canvas = stage.view || stage._canvas;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
      const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;
      
      globalPoint = {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY
      };
    }

    console.log('🔵 CornerNode calling onNodeMouseDown:', {
      nodeType: isRotationHandle ? 'rotation' : 'corner',
      index,
      globalPoint
    });

    onNodeMouseDown?.(event, isRotationHandle ? 'rotation' : 'corner', index, globalPoint);
  }, [index, onNodeMouseDown, containerRef, isRotationHandle, shape, nodeId, position]);

  const handleMouseMove = useCallback((event) => {
    if (!event) return;

    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else {
      // Convert local to global coordinates
      const stage = containerRef.current?.parent?.parent;
      if (!stage) return;
      
      const canvas = stage.view || stage._canvas;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
      const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;
      
      globalPoint = {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY
      };
    }

    onNodeMouseMove?.(event, globalPoint);
  }, [onNodeMouseMove, containerRef]);

  const handleMouseUp = useCallback((event) => {
    // Reset dragging state to show the node again
    setIsDragging(false);
    
    // Notify other nodes at the same position to show again
    window.dispatchEvent(new CustomEvent('nodeDragEnd', {
      detail: { nodeId, position }
    }));
    
    // Call the original mouse up handler
    onNodeMouseUp?.(event);
  }, [onNodeMouseUp, nodeId, position]);

  const drawNode = useCallback((g) => {
    g.clear();
    
    // If we should hide this node (another node at same position is being dragged), don't draw anything
    if (shouldHide) {
      return;
    }
    
    // If we're dragging, don't draw the visible node (keep hit area for interaction)
    if (isDragging && shape.nodes) {
      // Draw only the invisible hit area during dragging
      g.lineStyle(0);
      g.beginFill(0x000000, 0.01); // Invisible hit area
      g.drawCircle(0, 0, 12); // Larger hit area
      g.endFill();
      return;
    }
    
    if (isRotationHandle) {
      // Draw rotation handle (circular with arrow)
      g.lineStyle(1, 0xffffff);
      g.beginFill(isHovered ? 0x5ba0f2 : 0x2c5aa0);
      g.drawCircle(0, 0, 6);
      g.endFill();

      // Draw rotation arrow
      g.lineStyle(2, isHovered ? 0x5ba0f2 : 0x2c5aa0);
      g.arc(0, 0, 8, 0, Math.PI * 1.5);
      g.moveTo(8, -4);
      g.lineTo(8, 0);
      g.lineTo(12, 0);
    } else if (shape.nodes) {
      // Draw circle node for EditableLine nodes
      
      // Draw larger invisible hit area first
      g.lineStyle(0);
      g.beginFill(0x000000, 0.01); // Invisible hit area
      g.drawCircle(0, 0, 12); // Larger hit area
      g.endFill();
      
      // Draw visible circle node
      g.lineStyle(2, 0xffffff);
      g.beginFill(isHovered ? 0xff0000 : 0x00ff00); // Make it bright green/red for visibility
      g.drawCircle(0, 0, 8); // Visible node
      g.endFill();
    } else {
      // Draw square node for Rectangle corner nodes
      g.lineStyle(1, 0xffffff);
      g.beginFill(isHovered ? 0x5ba0f2 : 0x2c5aa0);
      g.drawRect(-4, -4, 8, 8);
      g.endFill();
    }
  }, [isHovered, isRotationHandle, shape, isDragging, shouldHide]);

  // Add global mouse up listener to handle mouse up outside the node
  React.useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        
        // Notify other nodes at the same position to show again
        window.dispatchEvent(new CustomEvent('nodeDragEnd', {
          detail: { nodeId, position }
        }));
      };

      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, nodeId, position]);

  // Don't render anything if this node should be hidden
  if (shouldHide) {
    return null;
  }

  return (
    <Graphics
      x={x}
      y={y}
      draw={drawNode}
      interactive={true}
      cursor={getCursor()}
      pointerdown={handleMouseDown}
      pointermove={handleMouseMove}
      pointerup={handleMouseUp}
      pointerover={() => setIsHovered(true)}
      pointerout={() => setIsHovered(false)}
      zIndex={shape.nodes ? 1000 : 100}
    />
  );
};

export default CornerNode; 